import React from 'react';
import { Form, FormikProps } from 'formik';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { StageSubmitRegistrar } from '../../_core/Formik/formik-submit-registrar.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { IFormOption } from '../../_core/_ui/forms.component';
import { DiagramUploadRow } from '../diagram-upload-row.component';
import { FormatTemplateMetadataFields } from '../format-template-metadata-fields.component';
import { FormatTemplateStageList } from '../format-template-stage-list.component';
import { messages } from '../format-template.messages';
import { IEditFormatTemplateFields } from './edit-format-template-modal.types';

const FORM_ID = 'edit-format-template-form';

interface IEditFormatTemplateModalProps {
  open: boolean;
  onClose: () => void;
  bucketOptions: string[];
  form: FormikProps<IEditFormatTemplateFields>;
  stages: IAssessmentStage[];
  updateStage: (id: string, changes: { config?: object; title?: string }) => void;
  registerStageSubmit: StageSubmitRegistrar;
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  positionOptions: IFormOption[];
  stagesLoading: boolean;
  currentImageUrl?: string;
  pendingDiagramUrl?: string;
  uploadingDiagram: boolean;
  diagramError: React.ReactNode;
  onSelectDiagram: (file: File) => void;
}

export const EditFormatTemplateModal: React.FC<IEditFormatTemplateModalProps> = ({
  open,
  onClose,
  bucketOptions,
  form,
  stages,
  updateStage,
  registerStageSubmit,
  drillOptions,
  practicePlanOptions,
  positionOptions,
  stagesLoading,
  currentImageUrl,
  pendingDiagramUrl,
  uploadingDiagram,
  diagramError,
  onSelectDiagram,
}) => {
  const { status, isSubmitting } = form;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Form lives inside the Dialog so the portal-rendered fields share its id for focus targeting. */}
      <Form id={FORM_ID}>
        <FocusFirstInvalidField formId={FORM_ID} />
        <DialogTitle><IntlMsg {...messages.editTitle} /></DialogTitle>
        <DialogContent>
          <FormatTemplateMetadataFields form={form} bucketOptions={bucketOptions} />

          <Box my={2}><Divider /></Box>

          <Typography variant="subtitle2"><IntlMsg {...messages.diagramTitle} /></Typography>
          <DiagramUploadRow
            alt="Template diagram"
            imageUrl={currentImageUrl}
            pendingPreviewUrl={pendingDiagramUrl}
            uploading={uploadingDiagram}
            disabled={isSubmitting}
            onSelectFile={onSelectDiagram}
          />
          {diagramError && (
            <FormHelperText error>{diagramError}</FormHelperText>
          )}

          <Box my={2}><Divider /></Box>

          <Typography variant="subtitle2"><IntlMsg {...messages.stagesTitle} /></Typography>
          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            <IntlMsg {...messages.stagesEditHint} />
          </Typography>
          <FormatTemplateStageList
            stages={stages}
            drillOptions={drillOptions}
            practicePlanOptions={practicePlanOptions}
            positionOptions={positionOptions}
            loading={stagesLoading}
            editable
            updateStage={updateStage}
            registerStageSubmit={registerStageSubmit}
          />

          {status && (
            <Box mt={1}>
              <FormHelperText error>{status}</FormHelperText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}><IntlMsg {...messages.cancel} /></Button>
          <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                <IntlMsg {...messages.saving} />
              </>
            ) : <IntlMsg {...messages.save} />}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
